import { inject, NgModule } from '@angular/core';
import {
  NavigationError,
  provideRouter,
  RedirectCommand,
  Router,
  RouterModule,
  Routes,
  withNavigationErrorHandler
} from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';

import { AppRouterComponent } from './module-application/components/app-router/app-router.component';
import { ENUM_ROLES } from './module-role/enums/enum-roles';
import { CustomerServiceType } from '@module-customer/enums/customer-service-type.enum';
import { environment } from '@environment/environment';
import { FeatureMode } from '@environment/environment.interface';

const featureRoutes: Routes = (() => {
  const routes: Routes = [
    {
      path: 'manufacturer-management',
      loadChildren: async () =>
        (await import('app/module-manufacturer/module-manufacturer'))
          .ModuleManufacturer,
      data: {
        featureFilter: [FeatureMode.REBATE],
        roles: [
          ENUM_ROLES.RXP_ADMIN,
          ENUM_ROLES.RXP_EXECUTIVE,
          ENUM_ROLES.RXP_STAFF,
          ENUM_ROLES.MFG_ADMIN,
          ENUM_ROLES.MFG_EXECUTIVE,
          ENUM_ROLES.MFG_STAFF
        ]
      }
    },
    {
      path: 'manufacturer-global-config-management',
      loadChildren: async () =>
        (
          await import(
            'app/module-manufacturer-program-global-config/module-manufacturer-program-global-config-router'
          )
        ).ModuleManufacturerProgramGlobalConfigRouter,
      data: {
        featureFilter: [FeatureMode.REBATE],
        roles: [ENUM_ROLES.RXP_ADMIN]
      }
    },
    {
      path: 'hcpcs-management',
      loadChildren: async () =>
        (await import('app/module-hcpcs/module-hcpcs')).ModuleHCPCS,
      data: {
        featureFilter: [FeatureMode.REBATE],
        roles: [
          ENUM_ROLES.RXP_ADMIN,
          ENUM_ROLES.RXP_EXECUTIVE,
          ENUM_ROLES.RXP_STAFF
        ]
      }
    },
    {
      path: 'contract-management',
      loadChildren: async () =>
        (
          await import(
            'app/module-contract-management/module-contract-management'
          )
        ).ModuleContractManagement,
      data: {
        featureFilter: [FeatureMode.REBATE]
      }
    },
    {
      path: 'benefit-designs-management',
      loadChildren: async () =>
        (
          await import(
            'app/module-benefit-design-management/module-benefit-design-management.module'
          )
        ).ModuleBenefitDesignManagement,
      data: {
        featureFilter: [FeatureMode.REBATE]
      }
    },
    {
      path: 'tpa',
      loadChildren: async () =>
        (await import('app/module-tpa/module-tpa-router'))
          .ModuleThirdPartyAdministratorRouter,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [ENUM_ROLES.RXP_ADMIN]
      }
    },
    {
      path: 'ce',
      loadChildren: async () =>
        (await import('app/module-covered-entity/module-covered-entity-router'))
          .ModuleCoveredEntityRouter,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [ENUM_ROLES.RXP_ADMIN]
      }
    },
    {
      path: 'mfp-pharmacy',
      loadChildren: async () =>
        (await import('app/module-mfp-pharmacy/module-mfp-pharmacy-router'))
          .ModuleMfpPharmacyRouter,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [ENUM_ROLES.RXP_ADMIN]
      }
    },
    {
      path: 'state-carve-in-management',
      loadChildren: async () =>
        (await import('app/module-state-carve-in/module-state-carve-in'))
          .ModuleStateCarveIn,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [
          ENUM_ROLES.RXP_ADMIN,
          ENUM_ROLES.RXP_EXECUTIVE,
          ENUM_ROLES.RXP_STAFF
        ]
      }
    },
    {
      path: 'drug-management',
      loadChildren: async () =>
        (await import('app/module-drug/module-drug-router')).ModuleDrugRouter,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [ENUM_ROLES.RXP_ADMIN, ENUM_ROLES.CE_ADMIN, ENUM_ROLES.CE_STAFF]
      }
    },
    {
      path: 'state-drug-exclusion-management',
      loadChildren: async () =>
        (
          await import(
            'app/module-state-drug-exclusion/module-state-drug-exclusion'
          )
        ).ModuleStateDrugExclusion,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [
          ENUM_ROLES.RXP_ADMIN,
          ENUM_ROLES.RXP_EXECUTIVE,
          ENUM_ROLES.RXP_STAFF
        ]
      }
    },
    {
      path: 'invoice-management',
      loadChildren: async () =>
        (
          await import(
            'app/module-invoice-management/module-invoice-management.module'
          )
        ).ModuleInvoiceManagement,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [ENUM_ROLES.RXP_ADMIN]
      }
    },
    {
      path: 'inbound-sms-management',
      loadChildren: async () =>
        (await import('app/module-inbound-sms/module-inbound-sms'))
          .ModuleInboundSms,
      data: {
        featureFilter: [FeatureMode.AUDIT_340B],
        roles: [ENUM_ROLES.RXP_ADMIN]
      }
    }
  ];

  return environment.featureMode
    ? routes.filter((route) =>
        (route.data?.featureFilter ?? []).includes(environment.featureMode)
      )
    : routes;
})();

const routes: Routes = [
  {
    path: '',
    component: AppRouterComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadChildren: async () =>
          (await import('app/module-application/module-application'))
            .ModuleApplication
      },
      {
        path: 'dashboard',
        loadChildren: async () =>
          (await import('app/module-dashboard/module-dashboard.module'))
            .ModuleDashboard
      },
      {
        path: 'dispute-dashboard',
        loadChildren: async () =>
          (
            await import(
              'app/module-dispute-dashboard/module-dispute-dashboard'
            )
          ).ModuleDisputeDashboard
      },
      {
        path: 'user-management',
        loadChildren: async () =>
          (await import('app/module-users-management/module-users-management'))
            .ModuleUsersManagement,
        data: {
          roles: [
            ENUM_ROLES.ADMIN,
            ENUM_ROLES.RXP_ADMIN,
            ENUM_ROLES.RXP_STAFF,
            ENUM_ROLES.RXP_EXECUTIVE
          ]
        }
      },
      {
        path: 'customer-management',
        loadChildren: async () =>
          (
            await import(
              'app/module-customer-management/module-customer-management'
            )
          ).ModuleCustomerManagement,
        data: {
          serviceType: CustomerServiceType.REBATE_OPERATIONS,
          roles: [
            ENUM_ROLES.RXP_ADMIN,
            ENUM_ROLES.RXP_EXECUTIVE,
            ENUM_ROLES.RXP_STAFF,
            ENUM_ROLES.CUSTOMER_ADMIN,
            ENUM_ROLES.CUSTOMER_EXECUTIVE,
            ENUM_ROLES.CUSTOMER_STAFF
          ]
        }
      },
      {
        path: 'audit-customer-management',
        loadChildren: async () =>
          (
            await import(
              'app/module-customer-management/module-customer-management'
            )
          ).ModuleCustomerManagement,
        data: {
          serviceType: CustomerServiceType.AUDIT_340B,
          roles: [
            ENUM_ROLES.RXP_ADMIN,
            ENUM_ROLES.RXP_EXECUTIVE,
            ENUM_ROLES.RXP_STAFF,
            ENUM_ROLES.CUSTOMER_ADMIN,
            ENUM_ROLES.CUSTOMER_EXECUTIVE,
            ENUM_ROLES.CUSTOMER_STAFF
          ]
        }
      },
      {
        path: 'health-system-management',
        loadChildren: async () =>
          (
            await import(
              'app/module-customer-management/module-customer-management'
            )
          ).ModuleCustomerManagement,
        data: {
          serviceType: CustomerServiceType.HEALTH_SYSTEM,
          roles: [
            ENUM_ROLES.RXP_ADMIN,
            ENUM_ROLES.RXP_EXECUTIVE,
            ENUM_ROLES.RXP_STAFF,
            ENUM_ROLES.CUSTOMER_ADMIN,
            ENUM_ROLES.CUSTOMER_EXECUTIVE,
            ENUM_ROLES.CUSTOMER_STAFF
          ]
        }
      },
      {
        path: 'pharmacy-chain-management',
        loadChildren: async () =>
          (
            await import(
              'app/module-customer-management/module-customer-management'
            )
          ).ModuleCustomerManagement,
        data: {
          serviceType: CustomerServiceType.PHARMACY_CHAIN,
          roles: [
            ENUM_ROLES.RXP_ADMIN,
            ENUM_ROLES.RXP_EXECUTIVE,
            ENUM_ROLES.RXP_STAFF,
            ENUM_ROLES.CUSTOMER_ADMIN,
            ENUM_ROLES.CUSTOMER_EXECUTIVE,
            ENUM_ROLES.CUSTOMER_STAFF
          ]
        }
      },
      {
        path: 'mfp-rebate',
        loadChildren: async () =>
          (await import('app/module-mfp-pharmacy/module-mfp-pharmacy-router'))
            .ModuleMfpPharmacyRouter
      },
      {
        path: 'claims',
        loadChildren: async () =>
          (await import('app/module-claims/module-claims-router'))
            .ModuleClaimsRouter
      },
      {
        path: 'access-denied',
        loadChildren: async () =>
          (await import('app/module-access-denied/access-denied.module'))
            .ModuleAccessDenied
      },
      ...featureRoutes
    ]
  },
  {
    path: 'login',
    loadChildren: async () =>
      (await import('app/module-login/module-login')).ModuleLogin
  },
  {
    path: '404',
    loadChildren: async () =>
      (await import('app/module-no-found/module-no-found.module'))
        .ModuleNoFoundModule
  },
  {
    path: '**',
    redirectTo: '/404'
  }
];

@NgModule({
  exports: [RouterModule],
  providers: [
    provideRouter(
      routes,
      withNavigationErrorHandler((e: NavigationError) => {
        if (!environment.production) {
          // eslint-disable-next-line no-console
          console.error('Router error:', e);
        }

        const router = inject(Router);
        const urlTree = router.parseUrl('/404');
        return new RedirectCommand(urlTree, { skipLocationChange: true });
      })
    )
  ]
})
export class AppRoutingModule {}
